<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { numeric, required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import PersonalSessionManager from '../channels/whatsapp/PersonalSessionManager.vue';
import TelegramPersonalSessionManager from '../channels/telegramPersonal/PersonalSessionManager.vue';
import MaxPersonalSessionManager from '../channels/max/PersonalSessionManager.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import { shouldBeUrl } from 'shared/helpers/Validators';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    SettingsAccordion,
    ImapSettings,
    SmtpSettings,
    NextButton,
    TextArea,
    WhatsappReauthorize,
    PersonalSessionManager,
    TelegramPersonalSessionManager,
    MaxPersonalSessionManager,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      allowMobileWebview: false,
      whatsAppInboxAPIKey: '',
      isRequestingReauthorization: false,
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      isSettingDefaults: false,
      telegramApiId: '',
      telegramApiHash: '',
      telegramGatewayUrl: '',
      isUpdatingTelegramCredentials: false,
      maxPhoneNumber: '',
      maxGatewayUrl: '',
      isUpdatingMaxConfiguration: false,
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
    telegramApiId: { required, numeric },
    telegramApiHash: { required },
    telegramGatewayUrl: { required, shouldBeUrl },
    maxPhoneNumber: { required },
    maxGatewayUrl: { required, shouldBeUrl },
  },
  computed: {
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappAppId() {
      return window.chatwootConfig?.whatsappAppId;
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
    isMaxBotChannel() {
      return this.inbox.provider === 'bot';
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    allowMobileWebview() {
      if (!this.isSettingDefaults) this.handleMobileWebviewFlag();
    },
    hmacMandatory() {
      if (!this.isSettingDefaults && this.isAWebWidgetInbox)
        this.handleHmacFlag();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.isSettingDefaults = true;
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowMobileWebview = (
        this.inbox.selected_feature_flags || []
      ).includes('allow_mobile_webview');
      this.allowedDomains = this.inbox.allowed_domains || '';
      this.telegramApiId = this.inbox.provider_config?.api_id || '';
      this.telegramApiHash = this.inbox.provider_config?.api_hash || '';
      this.telegramGatewayUrl =
        this.inbox.provider_config?.gateway_url ||
        window.chatwootConfig?.telegramPersonalGatewayUrl ||
        'http://telegram_personal_gateway:3200';
      this.maxPhoneNumber = this.inbox.phone_number || '';
      this.maxGatewayUrl =
        this.inbox.provider_config?.gateway_url ||
        window.chatwootConfig?.maxPersonalGatewayUrl ||
        'http://max_personal_gateway:3300';
      this.$nextTick(() => {
        this.isSettingDefaults = false;
      });
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMobileWebviewFlag() {
      try {
        const currentFlags = this.inbox.selected_feature_flags || [];
        const selectedFlags = this.allowMobileWebview
          ? [...currentFlags, 'allow_mobile_webview']
          : currentFlags.filter(f => f !== 'allow_mobile_webview');

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            selected_feature_flags: selectedFlags,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) {
        await this.$refs.whatsappReauth.requestAuthorization();
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async updateTelegramCredentials() {
      this.v$.telegramApiId.$touch();
      this.v$.telegramApiHash.$touch();
      this.v$.telegramGatewayUrl.$touch();

      if (
        this.v$.telegramApiId.$invalid ||
        this.v$.telegramApiHash.$invalid ||
        this.v$.telegramGatewayUrl.$invalid
      ) {
        return;
      }

      this.isUpdatingTelegramCredentials = true;

      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              api_id: this.telegramApiId?.trim(),
              api_hash: this.telegramApiHash?.trim(),
              gateway_url: this.telegramGatewayUrl?.trim(),
            },
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingTelegramCredentials = false;
      }
    },
    async updateMaxConfiguration() {
      this.v$.maxPhoneNumber.$touch();
      this.v$.maxGatewayUrl.$touch();

      if (this.v$.maxPhoneNumber.$invalid || this.v$.maxGatewayUrl.$invalid) {
        return;
      }

      this.isUpdatingMaxConfiguration = true;

      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            phone_number: this.maxPhoneNumber?.trim(),
            provider_config: {
              ...this.inbox.provider_config,
              gateway_url: this.maxGatewayUrl?.trim(),
            },
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingMaxConfiguration = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      v-if="isATwilioWhatsAppChannel"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAVoiceChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_TITLE')"
      :help-text="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_call_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_TITLE')"
      :help-text="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_status_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>

  <div v-else-if="isALineChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="space-y-4">
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            resize
            class="w-full [&>div]:!bg-transparent [&>div]:!border-none [&>div]:!border-0 [&>div]:px-0 [&>div]:pb-0 [&>div]:pt-0"
          />
          <div class="mt-3 flex justify-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="allowMobileWebview"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.LABEL')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.SUBTITLE')
        "
      />
    </div>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
      class="mt-6"
    >
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.SECRET_KEY') }}
          </p>
          <woot-code :script="inbox.hmac_token" />
          <p class="mt-1.5 text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
              class="text-n-blue-11 hover:underline text-label-small"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.VIEW_DOCS')
              }}
            </a>
          </p>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="hmacMandatory"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_LABEL')
        "
        :description="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_DESCRIPTION'
          )
        "
      />
    </SettingsAccordion>
  </div>
  <div v-else-if="isAPIInbox">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory" class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :help-text="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="py-2 px-3 bg-n-amber-3 outline-n-amber-4 text-n-amber-11 outline outline-1 -outline-offset-1 rounded-xl"
        >
          <p class="text-body-para mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="isAPersonalWhatsAppChannel">
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.TITLE')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.SUBHEADER')"
      >
        <PersonalSessionManager
          :inbox-id="inbox.id"
          :provider-config="inbox.provider_config || {}"
          :phone-number="inbox.phone_number"
        />
      </SettingsFieldSection>
    </div>
    <div v-else-if="inbox.provider_config">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          v-if="whatsappAppId"
          :label="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :help-text="`${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER')} ${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION')}`"
        >
          <div class="flex flex-col gap-1 items-start">
            <NextButton @click="handleReconfigure">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
        </NextButton>
      </SettingsFieldSection>
    </div>
    <WhatsappReauthorize
      v-if="isEmbeddedSignupWhatsApp"
      ref="whatsappReauth"
      :inbox="inbox"
      class="hidden"
    />
  </div>
  <div v-else-if="isATelegramPersonalChannel">
    <SettingsFieldSection
      :label="
        $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CREDENTIALS_TITLE')
      "
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CREDENTIALS_SUBHEADER')
      "
    >
      <div class="flex flex-col gap-4">
        <woot-input
          v-model="telegramApiId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_PERSONAL.API_ID.LABEL')"
          :placeholder="
            $t('INBOX_MGMT.ADD.TELEGRAM_PERSONAL.API_ID.PLACEHOLDER')
          "
          :error="v$.telegramApiId.$error"
          @blur="v$.telegramApiId.$touch"
        />
        <woot-input
          v-model="telegramApiHash"
          type="text"
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_PERSONAL.API_HASH.LABEL')"
          :placeholder="
            $t('INBOX_MGMT.ADD.TELEGRAM_PERSONAL.API_HASH.PLACEHOLDER')
          "
          :error="v$.telegramApiHash.$error"
          @blur="v$.telegramApiHash.$touch"
        />
        <woot-input
          v-model="telegramGatewayUrl"
          type="text"
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_PERSONAL.GATEWAY_URL.LABEL')"
          :placeholder="
            $t('INBOX_MGMT.ADD.TELEGRAM_PERSONAL.GATEWAY_URL.PLACEHOLDER')
          "
          :error="v$.telegramGatewayUrl.$error"
          @blur="v$.telegramGatewayUrl.$touch"
        />
        <div>
          <NextButton
            :disabled="isUpdatingTelegramCredentials"
            @click="updateTelegramCredentials"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.UPDATE_BUTTON')
            }}
          </NextButton>
        </div>
      </div>
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.SUBHEADER')"
    >
      <TelegramPersonalSessionManager
        :inbox-id="inbox.id"
        :provider-config="inbox.provider_config || {}"
      />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAMaxChannel && !isMaxBotChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.CREDENTIALS_TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.CREDENTIALS_SUBHEADER')"
    >
      <div class="flex flex-col gap-4">
        <woot-input
          v-model="maxPhoneNumber"
          type="text"
          :label="$t('INBOX_MGMT.ADD.MAX.PHONE_NUMBER.LABEL')"
          :placeholder="$t('INBOX_MGMT.ADD.MAX.PHONE_NUMBER.PLACEHOLDER')"
          :error="v$.maxPhoneNumber.$error"
          @blur="v$.maxPhoneNumber.$touch"
        />
        <woot-input
          v-model="maxGatewayUrl"
          type="text"
          :label="$t('INBOX_MGMT.ADD.MAX.GATEWAY_URL.LABEL')"
          :placeholder="$t('INBOX_MGMT.ADD.MAX.GATEWAY_URL.PLACEHOLDER')"
          :error="v$.maxGatewayUrl.$error"
          @blur="v$.maxGatewayUrl.$touch"
        />
        <div>
          <NextButton
            :disabled="isUpdatingMaxConfiguration"
            @click="updateMaxConfiguration"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.UPDATE_BUTTON') }}
          </NextButton>
        </div>
      </div>
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.SUBHEADER')"
    >
      <MaxPersonalSessionManager
        :inbox-id="inbox.id"
        :provider-config="inbox.provider_config || {}"
        :phone-number="inbox.phone_number"
      />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAMaxChannel && isMaxBotChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.BOT_TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.BOT_SUBHEADER')"
    >
      <div class="flex flex-col gap-2 text-sm text-n-slate-11">
        <p>
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.CONNECTED_ACCOUNT') }}:
          <code>{{ inbox.provider_config?.bot_name || inbox.name }}</code>
        </p>
        <p v-if="inbox.provider_config?.bot_username">
          Username:
          <code>@{{ inbox.provider_config.bot_username }}</code>
        </p>
        <p>
          Webhook:
          <code>{{ inbox.provider_config?.session_status || 'connected' }}</code>
        </p>
        <p v-if="inbox.provider_config?.session_error">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.LAST_ERROR') }}:
          <code>{{ inbox.provider_config.session_error }}</code>
        </p>
      </div>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAAvitoChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.AVITO.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.AVITO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" />
    </SettingsFieldSection>
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  ::v-deep input {
    margin-bottom: 0;
  }
}
</style>
